import { supabase } from "./supabase";
import type { Oekt } from "./session";

/**
 * Profilen: det som er ditt, på tvers av flatene.
 *
 * ========  DET DU HAR LAGET, OG DET VI VET OM DEG  ========
 *
 * To ulike ting i samme flate, og de hører sammen fordi de svarer på det
 * samme spørsmålet: hva har denne produksjonen av meg?
 *
 * Det første er arbeid — dokumenter du har lastet opp, rapporter du har
 * skrevet, timer du har ført. Det andre er personopplysninger, og der er
 * det art. 15 og 20 som gjelder: innsyn og dataportabilitet.
 */

export interface MittArbeid {
  readonly dokumenter: readonly { id: string; tittel: string; dato: string }[];
  readonly rapporter: readonly { id: string; dag: string; dato: string }[];
  readonly timer: readonly { id: string; start: string; slutt: string | null }[];
}

const TOMT: MittArbeid = { dokumenter: [], rapporter: [], timer: [] };

export async function hentMittArbeid(oekt: Oekt): Promise<MittArbeid> {
  const kl = supabase;
  if (kl === null) return TOMT;
  const medlem = oekt.member.id;

  const [dok, rap, tid] = await Promise.all([
    kl.from("document")
      .select("id, uploaded_at, file_path, series:series_id(title)")
      .eq("uploaded_by", medlem)
      .order("uploaded_at", { ascending: false }),
    kl.from("report")
      .select("id, created_at, session:session_id(shoot_day)")
      .eq("author_id", medlem)
      .order("created_at", { ascending: false }),
    kl.from("time_entry")
      .select("id, started_at, stopped_at")
      .eq("member_id", medlem)
      .order("started_at", { ascending: false }),
  ]);

  const rad = (x: unknown): Record<string, unknown> =>
    x as Record<string, unknown>;

  return {
    dokumenter: (dok.data ?? []).map((r) => {
      const s = rad(r)["series"] as { title?: string } | null;
      return {
        id: String(rad(r)["id"]),
        tittel: s?.title ?? String(rad(r)["file_path"]),
        dato: String(rad(r)["uploaded_at"]),
      };
    }),
    rapporter: (rap.data ?? []).map((r) => {
      const s = rad(r)["session"] as { shoot_day?: string } | null;
      return {
        id: String(rad(r)["id"]),
        dag: s?.shoot_day ?? "—",
        dato: String(rad(r)["created_at"]),
      };
    }),
    timer: (tid.data ?? []).map((r) => ({
      id: String(rad(r)["id"]),
      start: String(rad(r)["started_at"]),
      slutt: rad(r)["stopped_at"] == null ? null : String(rad(r)["stopped_at"]),
    })),
  };
}

/**
 * Alt vi har om deg, som fil.
 *
 * Art. 20, dataportabilitet: «i et strukturert, alminnelig anvendt og
 * maskinlesbart format». JSON er begge deler.
 *
 * Bygges av det DU kan lese, ikke av et eget uttrekk. Da kan den ikke
 * komme til å vise mer enn du har tilgang til — og den holder seg riktig
 * av seg selv når policyene endrer seg.
 */
export function somFil(oekt: Oekt, arbeid: MittArbeid): Blob {
  const innhold = {
    hentet: new Date().toISOString(),
    produksjon: { navn: oekt.production.name, id: oekt.production.id },
    person: {
      navn: oekt.member.displayName,
      forkortelse: oekt.member.initials,
      rolle: oekt.member.role,
    },
    arbeid,
  };
  return new Blob([JSON.stringify(innhold, null, 2)],
    { type: "application/json" });
}

export interface Sletteforespoersel {
  readonly id: string;
  readonly dato: string;
  readonly notat: string;
  readonly status: "open" | "done" | "refused";
  readonly svar: string | null;
}

export async function hentSletteforespoersler(): Promise<readonly Sletteforespoersel[]> {
  if (supabase === null) return [];
  const { data, error } = await supabase
    .from("erasure_request")
    .select("id, requested_at, note, status, answer")
    .order("requested_at", { ascending: false });
  if (error !== null || data === null) return [];
  return (data as unknown as Record<string, unknown>[]).map((r) => ({
    id: String(r["id"]),
    dato: String(r["requested_at"]),
    notat: String(r["note"]),
    status: String(r["status"]) as Sletteforespoersel["status"],
    svar: r["answer"] == null ? null : String(r["answer"]),
  }));
}

/**
 * Ber om sletting.
 *
 * ⚠ SLETTER INGENTING. `production_member.user_id` er `on delete restrict`
 * fordi loggen må kunne vise hvem som skrev en post. Avveiningen mellom
 * art. 17 og integriteten i loggen tilhører den behandlingsansvarlige —
 * produksjonsselskapet — ikke oss. Dette gir forespørselen et sted, en
 * dato og en eier. Se migrasjon 20261027200000.
 */
export async function beOmSletting(
  brukerId: string, notat: string,
): Promise<{ ok: boolean; feil?: string }> {
  if (supabase === null) return { ok: true };
  const { error } = await supabase
    .from("erasure_request")
    .insert({ user_id: brukerId, note: notat.trim() });
  return error === null ? { ok: true } : { ok: false, feil: error.message };
}
